import { ref, computed, shallowRef } from "vue";
import { defineStore } from "pinia";
import type { WorldeScoreDocument } from "@kekkle/shared/types/documents/wordle-score-document/wordle-score-document";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { dateObjectToDateString } from "@/helpers/converts/date-converter";
import { fetchGameIdPerDate } from "@/helpers/firestore/Wordle6GameService";
import { fetchGameScores } from "@/helpers/firestore/Wordle6ScoreService";
import {
  getBooleanFromLocalStorage,
  setLocalStorageItem,
} from "@/helpers/utils/local-storage";
import { useGroupsStore } from "@/stores/modules/groups/groups";

export const useWordle6ScoresStore = defineStore("wordle6Scores", () => {
  const groupsStore = useGroupsStore();
  const gameIds = ref<Record<string, string>>({});
  const onlyKnownUsers = ref<boolean>(
    getBooleanFromLocalStorage("onlyKnownUsersWordle6Scores", true),
  );
  const scoreDate = ref<Date>(new Date());
  const scores = ref<Record<string, WorldeScoreDocument[]>>({});
  const fetchingScores = ref<Record<string, boolean>>({ "123": false });
  const fetchingGameId = ref<Record<string, boolean>>({
    "01-01-2021": false,
  });
  const lastDocuments = shallowRef<
    Record<string, QueryDocumentSnapshot<DocumentData> | undefined>
  >({});
  const hasMoreData = ref<Record<string, boolean>>({});

  const currentGameId = computed<string | undefined>(() => {
    return gameIds.value[dateObjectToDateString(scoreDate.value, "dd-mm-yyyy")];
  });

  const currentScores = computed<WorldeScoreDocument[]>(() => {
    const gameId = currentGameId.value;
    if (!gameId || !scores.value[gameId]) return [];
    return scores.value[gameId];
  });

  const isFetchingScores = computed<boolean>(() => {
    return fetchingScores.value[currentGameId.value ?? ""] ?? false;
  });

  const isFetchingGameId = computed<boolean>(() => {
    return (
      fetchingGameId.value[
        dateObjectToDateString(scoreDate.value, "dd-mm-yyyy")
      ] ?? false
    );
  });

  const hasMore = computed<boolean>(() => {
    const gameId = currentGameId.value;
    return gameId ? (hasMoreData.value[gameId] ?? false) : false;
  });

  function setOnlyKnownUsersWordleScores(value: boolean): void {
    onlyKnownUsers.value = value;
    setLocalStorageItem("onlyKnownUsersWordle6Scores", JSON.stringify(value));
  }

  function setScoreDate(date: Date): void {
    scoreDate.value = date;
  }

  function reset(): void {
    gameIds.value = {};
    scoreDate.value = new Date();
    scores.value = {};
    fetchingScores.value = {};
    fetchingGameId.value = {};
    lastDocuments.value = {};
    hasMoreData.value = {};
    onlyKnownUsers.value = true;
    setLocalStorageItem("onlyKnownUsersWordle6Scores", "true");
  }

  async function fetchScores(date: Date, loadMore = false): Promise<void> {
    const dateString = dateObjectToDateString(date, "dd-mm-yyyy");
    let gameId: string | undefined = gameIds.value[dateString];
    if (!gameId) {
      fetchingGameId.value[dateString] = true;
      const fetchedGameId = await fetchGameIdPerDate(date);
      if (fetchedGameId) {
        gameId = fetchedGameId;
        gameIds.value[dateString] = gameId;
      }
      fetchingGameId.value[dateString] = false;
    }
    if (!gameId) return;
    fetchingScores.value[gameId] = true;

    let userFilter: string[] = [];
    if (onlyKnownUsers.value) {
      userFilter = groupsStore.allGroupMembersUuids;
    }

    const lastDoc = loadMore ? lastDocuments.value[gameId] : undefined;
    const result = await fetchGameScores(gameId, userFilter, {
      pageSize: 5,
      lastDoc,
    });

    result.docs.forEach((score) => {
      score.game_state = JSON.parse(score.game_state);
    });

    if (loadMore) {
      scores.value[gameId] = [...(scores.value[gameId] ?? []), ...result.docs];
    } else {
      scores.value[gameId] = result.docs;
    }

    lastDocuments.value[gameId] = result.lastDoc;
    hasMoreData.value[gameId] = result.hasMore;
    fetchingScores.value[gameId] = false;
  }

  return {
    gameIds,
    onlyKnownUsers,
    scoreDate,
    scores,
    fetchingScores,
    fetchingGameId,
    lastDocuments,
    hasMoreData,
    currentGameId,
    currentScores,
    isFetchingScores,
    isFetchingGameId,
    hasMore,
    setOnlyKnownUsersWordleScores,
    setScoreDate,
    reset,
    fetchScores,
  };
});
